import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { format, startOfMonth } from "date-fns";
import axios from "axios";
import * as XLSX from "xlsx";
import useApi from "../hooks/useApi"; // Adjust path if necessary
import {
  Search,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  Layers,
  FileText,
  Coins,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export default function QuotationReportPopup({ onClose }) {
  // Default dates: 1st of current month & Today's date
  const [dateFrom, setDateFrom] = useState(() =>
    format(startOfMonth(new Date()), "yyyy-MM-dd")
  );
  const [dateTo, setDateTo] = useState(() =>
    format(new Date(), "yyyy-MM-dd")
  );

  // Data & Filter Selection States
  const [reportData, setReportData] = useState([]);
  const [salesPerson, setSalesPerson] = useState("");
  const [coordinator, setCoordinator] = useState("");
  const [stage, setStage] = useState("");
  const [dept, setDept] = useState("");
  const [freeText, setFreeText] = useState("");

  // Show/hide the filter panel
  const [showFilters, setShowFilters] = useState(false);

  // Master List Options States (Fetched from APIs)
  const [salespersonList, setSalespersonList] = useState([]);
  const [coordinatorList, setCoordinatorList] = useState([]);
  const stageList = [
    "Draft",
    "For Approval",
    "Approved",
    "Customer Submission & Awaiting Approval",
    "Customer Approval",
    "Customer Negotiation",
    "Lost-Customer Rejection",
  ];

  const { request: requestQuotationList, loading, error } = useApi();

  // useApi's `request` function may not be memoized on its end, so it can get a
  // new identity on every render. Keep the latest version in a ref and read it
  // from there inside fetchQuotationList, so fetchQuotationList's own identity
  // stays stable across renders that don't actually change a filter.
  const requestQuotationListRef = useRef(requestQuotationList);
  useEffect(() => {
    requestQuotationListRef.current = requestQuotationList;
  }, [requestQuotationList]);

  // Fetch Master Lists (Coordinator & Salesperson) from API
  useEffect(() => {
    const companyCode = localStorage.getItem("companyCode") || "-";
    const apiPaths = localStorage.getItem("apiPaths")
      ? JSON.parse(localStorage.getItem("apiPaths"))
      : [];
    const currentDepartment =
      localStorage.getItem("currentSelectedDepartment") || "-";
    const baseUrl = apiPaths[0]?.API_CONFIG || "";

    if (baseUrl && companyCode !== "-") {
      // Fetch Coordinator List
      const coordinatorUrl = `${baseUrl}/api/MasterCount/${companyCode}/COORDINATORLIST/-/${currentDepartment}`;
      axios
        .get(coordinatorUrl)
        .then((res) => {
          setCoordinatorList([
            { EMP_ID: "GENERAL", NAME: "GENERAL" },
            ...(res.data || []),
          ]);
        })
        .catch((err) =>
          console.error("Error fetching coordinator list:", err)
        );

      // Fetch Salesperson List
      const salesPersonUrl = `${baseUrl}/api/MasterCount/${companyCode}/SALESPERSONLIST/-/${currentDepartment}`;
      axios
        .get(salesPersonUrl)
        .then((res) => {
          setSalespersonList(res.data || []);
        })
        .catch((err) =>
          console.error("Error fetching salesperson list:", err)
        );
    }
  }, []);

  // Self-contained quotation list fetch function with override fallback
  const fetchQuotationList = useCallback(
    async (overrideFilters = {}) => {
      try {
        const companyCode = localStorage.getItem("companyCode") || "-";
        const apiPaths = localStorage.getItem("apiPaths")
          ? JSON.parse(localStorage.getItem("apiPaths"))
          : [];
        const currentUserObject = localStorage.getItem("currentUser")
          ? JSON.parse(localStorage.getItem("currentUser"))
          : [];
        const currentUserDepartment =
          localStorage.getItem("currentSelectedDepartment") || "-";

        if (!currentUserObject.length || !apiPaths.length) {
          console.error("Missing local storage user/api configuration");
          return;
        }

        const empID = currentUserObject[0]?.EMPID || "-";
        const userAccess = currentUserObject[0]?.useraccess || "";

        // Resolve filter values (prefer explicitly passed overrides, fall back to state)
        const fromDateParam = overrideFilters.fromDate ?? dateFrom;
        const toDateParam = overrideFilters.toDate ?? dateTo;

        const activeSearch =
          overrideFilters.freeText !== undefined
            ? overrideFilters.freeText
            : freeText;
        const freeTextParam =
          activeSearch && activeSearch.trim() !== "" ? activeSearch.trim() : "-";

        const stageParam = overrideFilters.stage ?? (stage ? stage : "-");

        let salesPersonId = empID;
        if (userAccess.indexOf("ZK") === -1) {
          const activeSalesPerson =
            overrideFilters.salesPerson !== undefined
              ? overrideFilters.salesPerson
              : salesPerson;
          salesPersonId =
            activeSalesPerson && activeSalesPerson !== "" ? activeSalesPerson : "-";
        }

        let creatorId = empID;
        if (userAccess.indexOf("ZM") === -1) {
          const activeCoordinator =
            overrideFilters.coordinator !== undefined
              ? overrideFilters.coordinator
              : coordinator;
          creatorId =
            activeCoordinator && activeCoordinator !== "" ? activeCoordinator : "-";
        }

        const currentPageNumber = 1;
        const baseUrl = apiPaths[0]?.API_CONFIG || "";
        const url = `${baseUrl}/api/CRMTaskMainListFilter/${companyCode}/ProposalFilterReport/${salesPersonId}/${creatorId}/-/-/${currentUserDepartment}/${fromDateParam}/${toDateParam}/${encodeURIComponent(
          freeTextParam
        )}/${currentPageNumber}/${stageParam}`;

        const response = await requestQuotationListRef.current({
          url: url,
          method: "GET",
          timeout: 5000,
        });

        if (Array.isArray(response)) {
          setReportData(response);
        } else if (response && Array.isArray(response.data)) {
          setReportData(response.data);
        } else {
          setReportData([]);
        }
      } catch (err) {
        console.error("Error fetching report quotations inside popup:", err);
      }
    },
    [dateFrom, dateTo, freeText, stage, salesPerson, coordinator]
  );

  // Debounced effect: Auto-fetches API when any filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchQuotationList();
    }, 400);

    return () => clearTimeout(timer);
  }, [dateFrom, dateTo, salesPerson, coordinator, stage, freeText, fetchQuotationList]);

  // Selected object helpers for client-side filtering
  // Data is already filtered server-side (salesPerson, coordinator, stage,
  // freeText, and date range are all sent as URL params in fetchQuotationList).
  // Re-filtering here on exact-match fields like row.Coordinator / Coordinator_ID
  // is fragile (naming/casing may not match the API's response shape) and was
  // wiping out valid results. `dept` isn't wired to any UI control, so it's a
  // no-op filter for now — keep it in case a Dept selector gets added later.
  const filteredData = useMemo(() => {
    if (!dept) return reportData;
    return reportData.filter((row) => row.Dept === dept);
  }, [reportData, dept]);

  // Aggregate Metrics
  const totalAmount = useMemo(() => {
    return filteredData.reduce(
      (sum, item) => sum + Number(item.Amount || 0),
      0
    );
  }, [filteredData]);

  // Reset Filters
  const handleReset = () => {
    const defaultFrom = format(startOfMonth(new Date()), "yyyy-MM-dd");
    const defaultTo = format(new Date(), "yyyy-MM-dd");

    setDateFrom(defaultFrom);
    setDateTo(defaultTo);
    setSalesPerson("");
    setCoordinator("");
    setStage("");
    setDept("");
    setFreeText("");

    fetchQuotationList({
      fromDate: defaultFrom,
      toDate: defaultTo,
      freeText: "-",
      stage: "-",
      salesPerson: "",
      coordinator: "",
    });
  };

  // Export to CSV
  // Export to Excel (.xlsx)
  const exportToExcel = () => {
    if (filteredData.length === 0) return;

    const headers = Object.keys(filteredData[0]);

    // Format the Date column exactly like the on-screen table, as plain text
    // (no ="..." formula needed now that we control real column widths).
    const rows = filteredData.map((row) => {
      const formatted = {};
      headers.forEach((field) => {
        formatted[field] =
          field === "Date" ? formatDate(row[field]) : row[field] ?? "";
      });
      return formatted;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });

    // Auto-size each column to fit its content, with a sensible minimum so
    // the Date column (and others) never render as #### on open.
    worksheet["!cols"] = headers.map((field) => {
      const maxContentLength = rows.reduce(
        (max, row) => Math.max(max, String(row[field] ?? "").length),
        field.length
      );
      const minWidth = field === "Date" ? 14 : 10;
      return { wch: Math.max(maxContentLength + 2, minWidth) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Proposals");
    XLSX.writeFile(
      workbook,
      `Proposal_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const getStageBadge = (stageName) => {
    switch (stageName?.toLowerCase()) {
      case "won":
      case "customer approval":
      case "approved":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "for approval":
      case "customer submission & awaiting approval":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "draft":
        return "bg-slate-100 text-slate-700 border-slate-200";
      case "customer negotiation":
      case "negotiation":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "lost-customer rejection":
        return "bg-rose-100 text-rose-800 border-rose-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 70 }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Proposal Filter Report
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Filter, analyze, and export sales proposal data
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
              {showFilters ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => fetchQuotationList()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
            >
              Reset Filters
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-slate-500 bg-slate-100 hover:bg-rose-50 hover:text-rose-600 rounded-lg border border-slate-200 transition"
            >
              <X className="w-5 h-5" />
              <span className="hidden sm:inline">Close</span>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6 overflow-y-auto bg-slate-50/50">
          
          {/* Controls */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className="w-full flex items-center justify-between gap-2 text-slate-900 font-semibold border-b border-slate-100 pb-3"
            >
              <span className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-indigo-600" />
                <span>Search & Parameters</span>
              </span>
              {showFilters ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>

            {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">From Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                  <Calendar className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">To Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                  <Calendar className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                </div>
              </div>

              {/* Sales Person Dropdown - API Driven */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Sales Person</label>
                <select
                  value={salesPerson}
                  onChange={(e) => setSalesPerson(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white"
                >
                  <option value="">All Sales Persons</option>
                  {salespersonList.map((item, idx) => (
                    <option key={item.EMP_ID || idx} value={item.EMP_ID}>
                      {item.NAME}
                    </option>
                  ))}
                </select>
              </div>

              {/* Stage Dropdown - API Driven */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Stage</label>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white"
                >
                  <option value="">All Stages</option>
                  {stageList.map((stg, idx) => (
                    <option key={idx} value={stg}>
                      {stg}
                    </option>
                  ))}
                </select>
              </div>

              {/* Coordinator Dropdown - API Driven */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Coordinator / Creator</label>
                <select
                  value={coordinator}
                  onChange={(e) => setCoordinator(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none bg-white"
                >
                  <option value="">All Coordinators</option>
                  {coordinatorList.map((item, idx) => (
                    <option key={item.EMP_ID || idx} value={item.EMP_ID}>
                      {item.NAME}
                    </option>
                  ))}
                </select>
              </div>

              {/* Free Text Search */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Free Text Search</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search customer, proposal #, project, sales person..."
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-lg">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-500 font-medium">Filtered Proposals</div>
                <div className="text-lg font-bold text-slate-900">{filteredData.length} Records</div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
                <Coins className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-500 font-medium">Total Value</div>
                <div className="text-lg font-bold text-slate-900">
                   {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
              <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs text-slate-500 font-medium">Avg Proposal Value</div>
                <div className="text-lg font-bold text-slate-900">
                   {(filteredData.length ? totalAmount / filteredData.length : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto overflow-y-auto max-h-[400px]">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-2" />
                  <p className="text-sm">Fetching quotation data...</p>
                </div>
              ) : error ? (
                <div className="py-8 text-center text-rose-500 text-sm">
                  Error loading quotation list: {String(error)}
                </div>
              ) : (
                <table className="w-full text-left text-xs text-slate-600">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 uppercase font-semibold tracking-wider sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-4 bg-slate-50">Prop No / Rev</th>
                      <th className="py-3 px-4 bg-slate-50">Date</th>
                      <th className="py-3 px-4 bg-slate-50">Customer & Account</th>
                      <th className="py-3 px-4 bg-slate-50">Sales Person</th>
                      <th className="py-3 px-4 bg-slate-50">Coordinator</th>
                      <th className="py-3 px-4 bg-slate-50">Dept / Project / Lead</th>
                      <th className="py-3 px-4 bg-slate-50">Stage</th>
                      <th className="py-3 px-4 text-right bg-slate-50">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredData.length > 0 ? (
                      filteredData.map((row, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3 px-4 font-medium text-slate-900 whitespace-nowrap">
                            {row.Prop_No ? Math.floor(row.Prop_No) : "-"}
                            {row.Rev_No && (
                              <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded font-normal">
                                R{row.Rev_No}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">{formatDate(row.Date)}</td>
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-800">{row.Customer || "-"}</div>
                            <div className="text-[11px] text-slate-400">Acc: {row.Account || "-"}</div>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">{row.Sales_person_Name || row.Sales_Person || "-"}</td>
                          <td className="py-3 px-4 whitespace-nowrap">{row.Coordinator || "-"}</td>
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-800">{row.Project || "-"}</div>
                            <div className="text-[11px] text-slate-400">
                              Dept: {row.Dept || "-"} | Lead: #{row.Lead_ID ? Math.floor(row.Lead_ID) : "-"}
                            </div>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${getStageBadge(row.Stage)}`}>
                              {row.Stage || "Unknown"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-slate-900 whitespace-nowrap">
                            {row.Currency || "AED"} {Number(row.Amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-slate-400">
                          No proposals found matching the selected filter criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}